"use client";

import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { HostLabel } from "@cmp-src/labels/HostLabel/HostLabel";
import type { Visit } from "@gettactic/api";
import { useOfficesQuery } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Building2, Mail, Phone } from "lucide-react";
import { api } from "@/lib/api/api";

interface DashboardVisitDetailsProps {
  visit: Visit;
}

export const DashboardVisitDetails: React.FC<DashboardVisitDetailsProps> = ({
  visit: incomingVisit
}) => {
  const { data: visitUpdated } = useQuery({
    queryKey: ["visit", incomingVisit.id],
    queryFn: () => api.client.visits.get(incomingVisit.id),
    enabled: !!incomingVisit.id
  });

  const visit = visitUpdated?.result ?? incomingVisit;

  const { data: officesData } = useOfficesQuery();
  const officeName = visit.office_id
    ? officesData?.byId[visit.office_id]?.name
    : undefined;

  const expectedTime = visit.expected
    ? format(parseISO(visit.expected), "h:mm a")
    : null;

  const expectedDate = visit.expected
    ? format(parseISO(visit.expected), "MMMM d, yyyy")
    : null;

  const checkedInTime = visit.checked_in
    ? format(parseISO(visit.checked_in), "h:mm a")
    : null;

  return (
    <div className="flex flex-col gap-6">
      {/* Visitor Contact Card */}
      <div className="rounded-lg border border-neutral-200 p-6">
        <div className="flex items-center gap-8">
          <div className="size-28 shrink-0">
            <UserAvatar
              user={{
                name: visit.visitor.name || "Visitor",
                profile_photo_url: visit.guest_photo_url || null
              }}
              size="full"
              isPlaneImage
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="font-semibold text-lg text-neutral-900">
              {visit.visitor.name || "Unknown Visitor"}
            </span>
            {visit.visitor_type && (
              <span className="text-neutral-500 text-sm">
                {visit.visitor_type.name}
              </span>
            )}
            <div className="mt-3 flex flex-col gap-1.5">
              {officeName && (
                <div className="flex items-center gap-2 text-neutral-600 text-sm">
                  <Building2 className="h-4 w-4 shrink-0 text-neutral-400" />
                  <span>{officeName}</span>
                </div>
              )}
              {visit.visitor.email && (
                <div className="flex items-center gap-2 text-neutral-600 text-sm">
                  <Mail className="h-4 w-4 shrink-0 text-neutral-400" />
                  <span className="truncate">{visit.visitor.email}</span>
                </div>
              )}
              {visit.host?.email && (
                <div className="flex items-center gap-2 text-neutral-600 text-sm">
                  <Phone className="h-4 w-4 shrink-0 text-neutral-400" />
                  <span>{visit.host.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Visit Information */}
      <div className="rounded-lg border border-neutral-200 p-6">
        <h3 className="mb-5 font-semibold text-neutral-900">
          Visitor Information
        </h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          <div className="flex flex-col gap-1">
            <span className="font-medium text-neutral-400 text-xs uppercase tracking-wider">
              Type of Visit
            </span>
            <span className="text-neutral-900 text-sm">
              {visit.visitor_type?.name ?? "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-neutral-400 text-xs uppercase tracking-wider">
              Visit Date
            </span>
            <span className="text-neutral-900 text-sm">
              {expectedDate ?? "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-neutral-400 text-xs uppercase tracking-wider">
              Office
            </span>
            <span className="text-neutral-900 text-sm">
              {officeName ?? "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-neutral-400 text-xs uppercase tracking-wider">
              Estimated Arrival Time
            </span>
            <span className="text-neutral-900 text-sm">
              {visit.status === "checked_in" && checkedInTime
                ? checkedInTime
                : (expectedTime ?? "—")}
            </span>
          </div>
          {visit.visit_fields.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="font-medium text-neutral-400 text-xs uppercase tracking-wider">
                Reason of Visit
              </span>
              <span className="text-neutral-900 text-sm">
                {visit.visit_fields[0]?.value || "—"}
              </span>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="font-medium text-neutral-400 text-xs uppercase tracking-wider">
              Host
            </span>
            {visit.host ? (
              <HostLabel host={visit.host} />
            ) : (
              <span className="text-neutral-900 text-sm">—</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
